'use client'

import React from 'react'
import { usePathname } from 'next/navigation'

import { pxToRem } from '@ui/style/muiTheme'
import { Box } from '@mui/joy'
import UIFlex from '@components/ui/UIFlex'
import UIText from '@components/ui/UIText'
import { SxProps } from '@mui/joy/styles/types'
import { ElectionMetaResponseVo } from '@schemas/non-auth'
import { useSendElectionBanner } from '@hooks/ga4/election'

interface TopBannerProps {
	data: ElectionMetaResponseVo
	viewPoint: string
}

const TopBanner = ({ data, viewPoint }: TopBannerProps) => {
	const pathname = usePathname()
	const { sendElectionBanner } = useSendElectionBanner()

	const setGA4 = () => {
		if (viewPoint === 'home') {
			if (pathname.includes('article')) {
				sendElectionBanner('article_banner_topic', { btn: '프로모션배너' })
			} else {
				sendElectionBanner('home_banner', { btn: '프로모션배너' })
			}
		} else if (viewPoint === 'article') {
			sendElectionBanner('article_banner_issue', { btn: '프로모션배너' })
		} else if (viewPoint === 'election') {
			sendElectionBanner('topic_banner_issue', { btn: '프로모션배너' })
		}
	}
	const setBanner = () => {
		switch (viewPoint) {
			case 'home':
				return {
					height: {
						xs: pxToRem(140),
						md: pxToRem(177),
						lg: pxToRem(240),
						xl: pxToRem(288),
					},
					backgroundImage: {
						xs: 'url(/images/election/election_banner_main_bg_mo.png)',
						md: 'url(/images/election/election_banner_main_bg_pc.png)',
					},
					backgroundSize: {
						xs: 'cover',
						md: '1213px',
						lg: '1975px',
					},
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
					backgroundColor: '#DDF8F0',
				}
			case 'election':
				return {
					height: {
						xs: pxToRem(393),
						md: pxToRem(304),
						lg: pxToRem(360),
						xl: pxToRem(410),
					},
					backgroundImage: {
						xs: 'url(/images/election/election_banner_bg_mo.jpg)',
						md: 'url(/images/election/election_banner_bg_pc.png)',
					},
					backgroundSize: {
						xs: 'cover',
						lg: '1920px',
					},
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
					backgroundColor: '#DDF8F0',
				}
			default:
				return {
					height: {
						xs: pxToRem(88),
						md: pxToRem(88),
						lg: pxToRem(118),
						xl: pxToRem(140),
					},
					backgroundImage: {
						xs: 'url(/images/election/election_banner_article_bg_mo.jpg)',
						md: 'url(/images/election/election_banner_article_bg_pc.jpg)',
					},
					backgroundSize: {
						xs: 'cover',
						lg: '1600px',
					},
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
					backgroundColor: '#DDF8F0',
				}
		}
	}
	const setRemainingDay = () => {
		switch (viewPoint) {
			case 'home':
				return {
					padding: {
						xs: `${pxToRem(6)} ${pxToRem(12)}`,
						md: `${pxToRem(8)} ${pxToRem(16)}`,
						lg: `${pxToRem(10)} ${pxToRem(22)}`,
					},
					fontSize: {
						xs: pxToRem(14),
						md: pxToRem(18),
						lg: pxToRem(22),
					},
					lineHeight: {
						xs: pxToRem(14),
						md: pxToRem(18),
						lg: pxToRem(22),
					},
				}
			case 'election':
				return {
					padding: {
						xs: `${pxToRem(8)} ${pxToRem(16)}`,
						lg: `${pxToRem(10)} ${pxToRem(22)}`,
					},
					fontSize: {
						xs: pxToRem(20),
						lg: pxToRem(28),
					},
					lineHeight: {
						xs: pxToRem(20),
						lg: pxToRem(28),
					},
				}
			default:
				return {
					padding: {
						xs: `${pxToRem(8)} ${pxToRem(16)}`,
						lg: `${pxToRem(10)} ${pxToRem(22)}`,
					},
					fontSize: {
						xs: pxToRem(18),
						lg: pxToRem(22),
					},
					lineHeight: {
						xs: pxToRem(18),
						lg: pxToRem(22),
					},
					marginRight: {
						xs: pxToRem(20),
						md: pxToRem(0),
					},
				}
		}
	}
	const setElectionTitle = () => {
		switch (viewPoint) {
			case 'home':
				return {
					fontSize: {
						xs: pxToRem(16),
						md: pxToRem(16),
						lg: pxToRem(22),
					},
					lineHeight: {
						xs: pxToRem(16),
						md: pxToRem(20),
						lg: pxToRem(22),
					},
					marginBottom: {
						xs: pxToRem(8),
						md: pxToRem(8),
						lg: pxToRem(16),
					},
				}
			case 'election':
				return {
					fontSize: {
						xs: pxToRem(20),
						lg: pxToRem(24),
					},
					lineHeight: {
						xs: pxToRem(16),
						md: pxToRem(22),
					},
					marginBottom: {
						xs: pxToRem(20),
						md: pxToRem(16),
						lg: pxToRem(20),
					},
				}
			default:
				return {
					fontSize: {
						md: pxToRem(16),
						lg: pxToRem(20),
					},
					lineHeight: {
						xs: pxToRem(14),
						md: pxToRem(18),
						lg: pxToRem(22),
					},
					marginRight: {
						md: pxToRem(28),
					},
					marginBottom: {
						xs: pxToRem(8),
						md: pxToRem(0),
					},
				}
		}
	}
	const setElectionTitleImage = () => {
		switch (viewPoint) {
			case 'home':
				return {
					backgroundImage: {
						xs: 'url(/images/election/election_banner_title_pc.png)',
						md: 'url(/images/election/election_banner_title_pc.png)',
					},
					width: {
						xs: pxToRem(230),
						md: pxToRem(281),
						lg: pxToRem(392),
					},
					height: {
						xs: pxToRem(27),
						md: pxToRem(33),
						lg: pxToRem(46),
					},
				}
			case 'election':
				return {
					backgroundImage: {
						xs: 'url(/images/election/election_banner_title_mo.png)',
						md: 'url(/images/election/election_banner_title_pc.png)',
					},
					width: {
						xs: pxToRem(219),
						md: pxToRem(363),
						lg: pxToRem(524),
						xl: pxToRem(524),
					},
					height: {
						xs: pxToRem(97),
						md: pxToRem(43),
						lg: pxToRem(62),
						xl: pxToRem(62),
					},
				}
			default:
				return {
					backgroundImage: {
						xs: 'url(/images/election/election_banner_title_pc.png)',
						md: 'url(/images/election/election_banner_title_pc.png)',
					},
					width: {
						xs: pxToRem(203),
						md: pxToRem(253),
						lg: pxToRem(363),
					},
					height: {
						xs: pxToRem(24),
						md: pxToRem(30),
						lg: pxToRem(43),
					},
				}
		}
	}
	const setTextBox = () => {
		switch (viewPoint) {
			case 'home':
				return {
					marginBottom: {
						xs: pxToRem(12),
						md: pxToRem(16),
						lg: pxToRem(28),
					},
					textAlign: 'center',
				}
			case 'election':
				return {
					marginBottom: {
						xs: pxToRem(40),
						md: pxToRem(30),
						lg: pxToRem(44),
					},
					textAlign: 'center',
				}
			default:
				return {
					display: 'flex',
					alignItems: {
						xs: 'flex-start',
						md: 'center',
					},
					justifyContent: 'center',
					flexDirection: {
						xs: 'column',
						md: 'row',
					},
					marginRight: {
						xs: pxToRem(0),
						md: pxToRem(32),
						lg: pxToRem(40),
					},
					marginLeft: {
						xs: pxToRem(20),
						md: pxToRem(0),
					},
				}
		}
	}

	return (
		<>
			{data.topBannerYn === 'Y' && (
				<Box
					sx={{
						...setBanner(),
						cursor: 'pointer',
					}}
					onClick={() => {
						setGA4()
						window.location.href = '/election'
					}}
				>
					<UIFlex
						flexDirection={
							viewPoint === 'election' || viewPoint === 'home' ? 'column' : 'row'
						}
						alignItems="center"
						height="100%"
						justifyContent={
							viewPoint === 'election' || viewPoint === 'home'
								? 'center'
								: { xs: 'space-between', md: 'center' }
						}
					>
						<Box sx={setTextBox() as SxProps}>
							<UIText
								color="#082B4F"
								fontWeight={700}
								sx={{
									...setElectionTitle(),
								}}
							>
								제 21대 대통령 선거
							</UIText>
							<Box
								sx={{
									...setElectionTitleImage(),
									backgroundSize: 'contain',
									backgroundPosition: 'center',
									backgroundRepeat: 'no-repeat',
									textIndent: '-9999rem',
								}}
							>
								2025 우리의 선택
							</Box>
						</Box>
						<Box
							sx={{
								borderRadius: pxToRem(100),
								bgcolor: '#082B4F',
								fontWeight: 700,
								color: '#ffffff',
								...setRemainingDay(),
							}}
						>
							D-{data.electionRemainingDay}
						</Box>
						{viewPoint === 'election' && (
							<UIText
								fontSize={{
									md: pxToRem(16),
									lg: pxToRem(20),
								}}
								color="#082B4F"
								fontWeight={700}
								lineHeight={pxToRem(22)}
								marginTop={{
									xs: pxToRem(16),
									lg: pxToRem(22),
								}}
							>
								선거일 6.3 (화)
							</UIText>
						)}
					</UIFlex>
				</Box>
			)}
		</>
	)
}

export default TopBanner
